import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, Skeleton, Typography } from '@mui/material';
import { ArrowBack, Assignment } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { portalApi, WorkOrderItem } from '../services/portalApi';
import { formatStatus } from '../utils/formatStatus';

export const WorkOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [workOrder, setWorkOrder] = useState<WorkOrderItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setWorkOrder(null);
    setError(null);
    if (!id) return;
    portalApi.getWorkOrderDetail(id)
      .then(data => { if (!cancelled) setWorkOrder(data); })
      .catch(err => { if (!cancelled) setError(err.message || t('Unable to load work order')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, t]);

  const date = (value: string) => new Date(value).toLocaleDateString(
    i18n.resolvedLanguage === 'es' ? 'es-MX' : 'en-US',
    { year: 'numeric', month: 'long', day: 'numeric' },
  );

  return (
    <Box sx={{ overflowWrap: 'anywhere' }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/work-orders')} sx={{ mb: 2 }}>
        {t('Back to Work Orders')}
      </Button>
      {loading ? <Skeleton variant="rounded" height={240} /> : error ? (
        <Alert severity="error">{error}</Alert>
      ) : workOrder && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
              <Typography variant="h4" component="h1">{t(formatStatus(workOrder.type))}</Typography>
              <Chip size="small" label={t(formatStatus(workOrder.status))}
                color={workOrder.status === 'completed' ? 'success' : 'info'} />
            </Box>
            {workOrder.property && <Typography sx={{ mb: 2 }}>
              {workOrder.property.name && <Box component="span" sx={{ display: 'block' }}>{workOrder.property.name}</Box>}
              {workOrder.property.address}
            </Typography>}
            <Typography color="text.secondary">{t('Scheduled')}: {workOrder.scheduledDate ? date(workOrder.scheduledDate) : t('Not scheduled')}</Typography>
            {workOrder.completedAt && <Typography color="text.secondary">{t('Completed')}: {date(workOrder.completedAt)}</Typography>}
            {workOrder.technicianName && <Typography color="text.secondary">{t('Technician')}: {workOrder.technicianName}</Typography>}
            {workOrder.estimatedDuration > 0 && <Typography color="text.secondary">{t('Est. Duration')}: {workOrder.estimatedDuration} min</Typography>}
            {workOrder.description && <Box sx={{ mt: 3 }}>
              <Typography variant="h6" component="h2" gutterBottom>{t('Description')}</Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{workOrder.description}</Typography>
            </Box>}
            {workOrder.notes && <Box sx={{ mt: 3 }}>
              <Typography variant="h6" component="h2" gutterBottom>{t('Notes')}</Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{workOrder.notes}</Typography>
            </Box>}
            {workOrder.serviceLogId && <Button variant="contained" startIcon={<Assignment />} sx={{ mt: 3 }}
              onClick={() => navigate(`/service-history/${workOrder.serviceLogId}`)}>
              {t(workOrder.logType === 'inspection' ? 'View Inspection Report' : 'View Service Report')}
            </Button>}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
